/**
 * OPFS Polyfill for iOS Compatibility
 * Routes async OPFS calls through a worker pool using sync access handles
 */

import debugx from "debugx";
// @ts-ignore - webpack will handle this import
import OPFSWorker from "./opfs-polyfill.worker";

const debug = debugx("bikedash:opfs-polyfill");

interface OPFSRequest {
  id: string;
  method: string;
  path: string[];
  args?: any[];
}

interface OPFSResponse {
  id: string;
  success: boolean;
  result?: any;
  error?: string;
}

// Detect if we need the polyfill (all Safari browsers for consistent behavior)
function needsPolyfill(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);

  // Use polyfill for all Safari (iOS and desktop) for consistent experience
  // iOS requires sync access handles in workers
  // Desktop Safari can use async but we want same behavior on all Apple devices
  if (isIOS || isSafari) {
    debug(
      "Detected Safari/iOS - using OPFS polyfill for consistent i-device experience",
    );
    return true;
  }

  debug("Not Safari - using native OPFS async API");
  return false;
}

class WorkerPool {
  private workers: Worker[] = [];
  private currentWorker = 0;
  private pendingRequests = new Map<
    string,
    { resolve: Function; reject: Function }
  >();
  private requestCounter = 0;

  constructor(poolSize: number = 4) {
    debug("Initializing OPFS worker pool with %d workers", poolSize);

    for (let i = 0; i < poolSize; i++) {
      // @ts-ignore
      const worker = new OPFSWorker();

      worker.addEventListener(
        "message",
        (event: MessageEvent<OPFSResponse>) => {
          this.handleResponse(event.data);
        },
      );

      worker.addEventListener("error", (error: any) => {
        debug.error("Worker %d error:", i, error);
      });

      this.workers.push(worker);
    }

    debug("Worker pool initialized with %d workers", this.workers.length);
  }

  private handleResponse(response: OPFSResponse): void {
    const pending = this.pendingRequests.get(response.id);
    if (!pending) {
      debug.warn("Received response for unknown request: %s", response.id);
      return;
    }

    this.pendingRequests.delete(response.id);

    if (response.success) {
      pending.resolve(response.result);
    } else {
      debug.error("Request %s failed: %s", response.id, response.error);
      pending.reject(new Error(response.error || "Unknown error"));
    }
  }

  async sendRequest(
    method: string,
    path: string[],
    args?: any[],
  ): Promise<any> {
    if (this.workers.length === 0) {
      throw new Error("Worker pool not initialized - no workers available");
    }

    const id = `req_${this.requestCounter++}_${Date.now()}`;
    const request: OPFSRequest = { id, method, path, args };

    debug("Sending request %s: %s", id, method);

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject });

      // Round-robin to next worker
      const worker = this.workers[this.currentWorker];
      this.currentWorker = (this.currentWorker + 1) % this.workers.length;

      worker.postMessage(request);

      // Timeout after 30 seconds
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error("Request timeout"));
        }
      }, 30000);
    });
  }

  terminate(): void {
    for (const worker of this.workers) {
      worker.terminate();
    }
    this.workers = [];
    this.pendingRequests.clear();
    debug("Worker pool terminated");
  }
}

// Global worker pool instance
let workerPool: WorkerPool | null = null;

function getWorkerPool(): WorkerPool {
  if (!workerPool) {
    workerPool = new WorkerPool(4);
  }
  return workerPool;
}

/**
 * Polyfilled FileSystemWritableFileStream
 */
class PolyfillWritableFileStream {
  private writerId: string;
  private pool: WorkerPool;
  private path: string[];
  private position: number = 0;

  constructor(
    writerId: string,
    pool: WorkerPool,
    path: string[],
    size: number = 0,
  ) {
    this.writerId = writerId;
    this.pool = pool;
    this.path = path;
    this.position = size;
  }

  async write(data: string | ArrayBuffer | Uint8Array): Promise<void> {
    // Transfer data to worker
    const result = await this.pool.sendRequest("write", this.path, [
      this.writerId,
      data,
    ]);
    this.position += result.bytesWritten;
  }

  async seek(position: number): Promise<void> {
    await this.pool.sendRequest("seek", this.path, [this.writerId, position]);
    this.position = position;
  }

  async close(): Promise<void> {
    await this.pool.sendRequest("close", this.path, [this.writerId]);
  }
}

/**
 * Polyfilled FileSystemFileHandle
 */
class PolyfillFileSystemFileHandle {
  readonly kind = "file";
  private path: string[];
  private pool: WorkerPool;

  constructor(path: string[], pool: WorkerPool) {
    this.path = path;
    this.pool = pool;
  }

  async createWritable(options?: {
    keepExistingData?: boolean;
  }): Promise<PolyfillWritableFileStream> {
    debug("createWritable(%o) on path %o", options, this.path);
    const result = await this.pool.sendRequest("createWritable", this.path, [
      options,
    ]);
    debug(
      "Created writable stream: %s (size: %d)",
      result.writerId,
      result.size || 0,
    );
    return new PolyfillWritableFileStream(
      result.writerId,
      this.pool,
      this.path,
      result.size || 0,
    );
  }

  async getFile(): Promise<{ text: () => Promise<string>; size: number }> {
    debug("getFile() on path %o", this.path);
    const result = await this.pool.sendRequest("getFile", this.path);
    return {
      text: async () => result.text,
      size: result.size,
    };
  }
}

/**
 * Polyfilled FileSystemDirectoryHandle
 */
class PolyfillFileSystemDirectoryHandle {
  readonly kind = "directory";
  private path: string[];
  private pool: WorkerPool;

  constructor(path: string[], pool: WorkerPool) {
    this.path = path;
    this.pool = pool;
  }

  async getDirectoryHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<PolyfillFileSystemDirectoryHandle> {
    debug("getDirectoryHandle(%s, %o) on path %o", name, options, this.path);
    await this.pool.sendRequest("getDirectoryHandle", this.path, [
      name,
      options,
    ]);
    return new PolyfillFileSystemDirectoryHandle(
      [...this.path, name],
      this.pool,
    );
  }

  async getFileHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<PolyfillFileSystemFileHandle> {
    debug("getFileHandle(%s, %o) on path %o", name, options, this.path);
    await this.pool.sendRequest("getFileHandle", this.path, [name, options]);
    return new PolyfillFileSystemFileHandle([...this.path, name], this.pool);
  }

  async removeEntry(
    name: string,
    options?: { recursive?: boolean },
  ): Promise<void> {
    await this.pool.sendRequest("removeEntry", this.path, [name, options]);
  }

  async *values(): AsyncIterableIterator<
    PolyfillFileSystemDirectoryHandle | PolyfillFileSystemFileHandle
  > {
    const result = await this.pool.sendRequest("values", this.path);

    for (const entry of result.entries) {
      if (entry.kind === "directory") {
        yield new PolyfillFileSystemDirectoryHandle(
          [...this.path, entry.name],
          this.pool,
        );
      } else {
        yield new PolyfillFileSystemFileHandle(
          [...this.path, entry.name],
          this.pool,
        );
      }
    }
  }

  // Add iterator support for for-await-of
  [Symbol.asyncIterator]() {
    return this.entries();
  }

  async *entries(): AsyncIterableIterator<
    [string, PolyfillFileSystemDirectoryHandle | PolyfillFileSystemFileHandle]
  > {
    const result = await this.pool.sendRequest("values", this.path);

    for (const entry of result.entries) {
      if (entry.kind === "directory") {
        yield [
          entry.name,
          new PolyfillFileSystemDirectoryHandle(
            [...this.path, entry.name],
            this.pool,
          ),
        ];
      } else {
        yield [
          entry.name,
          new PolyfillFileSystemFileHandle(
            [...this.path, entry.name],
            this.pool,
          ),
        ];
      }
    }
  }
}

/**
 * Polyfilled storage.getDirectory()
 */
async function getDirectory(): Promise<
  FileSystemDirectoryHandle | PolyfillFileSystemDirectoryHandle
> {
  if (!needsPolyfill()) {
    // Use native OPFS
    return navigator.storage.getDirectory();
  }

  // Use polyfill
  const pool = getWorkerPool();
  await pool.sendRequest("getDirectory", []);
  return new PolyfillFileSystemDirectoryHandle([], pool);
}

/**
 * Install the polyfill
 */
export function installOPFSPolyfill(): void {
  if (!needsPolyfill()) {
    debug("Polyfill not needed on this platform");
    return;
  }

  debug("Installing OPFS polyfill for Safari compatibility");

  // Replace navigator.storage.getDirectory with polyfilled version
  const originalGetDirectory = navigator.storage.getDirectory.bind(
    navigator.storage,
  );

  (navigator.storage as any).getDirectory = async function () {
    debug("getDirectory() intercepted - routing through polyfill");
    return getDirectory();
  };
  (navigator.storage as any).__originalGetDirectory = originalGetDirectory;

  debug("OPFS polyfill installed - navigator.storage.getDirectory replaced");
}

/**
 * Uninstall the polyfill (for testing)
 */
export function uninstallOPFSPolyfill(): void {
  if ((navigator.storage as any).__originalGetDirectory) {
    navigator.storage.getDirectory = (
      navigator.storage as any
    ).__originalGetDirectory;
    delete (navigator.storage as any).__originalGetDirectory;
  }

  if (workerPool) {
    workerPool.terminate();
    workerPool = null;
  }

  debug("OPFS polyfill uninstalled");
}

// Auto-install on import if needed
if (needsPolyfill()) {
  installOPFSPolyfill();
}
